/**
 * Call record.
 *
 * Filter chips are client-side over the loaded page, as designed. The sentiment
 * dot only appears once sentiment exists; before that the key explains its
 * absence rather than showing a neutral dot for every caller, which would read
 * as a measurement.
 */
import { border, c, categoryFor, mono, radius, sentimentColour, shadow } from '../theme/tokens';
import { Eyebrow, H1, Mono, Pill } from '../components/primitives';
import { EmptyRange, Failure, LoadingPanel, NotReady } from '../components/states';
import { LinkButton } from '../components/primitives';
import type { CallFilter, CallsResult, DashboardContext, RangeKey } from '../api/types';

const FILTERS: CallFilter[] = ['All', 'Needs follow-up', 'Viewing requests', 'Messages', 'Positive', 'Neutral', 'Frustrated'];

const GRID = '54px minmax(96px,1.1fr) 118px minmax(120px,1.9fr) 124px';

export function Calls({
  ctx,
  calls,
  filter,
  loading,
  error,
  onFilter,
  onOpenCall,
  onRetry,
  onLoadMore,
  onChangeRange,
}: {
  ctx: DashboardContext;
  calls: CallsResult | null;
  filter: CallFilter;
  loading: boolean;
  error: string | null;
  onFilter: (f: CallFilter) => void;
  onOpenCall: (id: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
  onChangeRange: (next: RangeKey) => void;
}) {
  const sentimentReady = ctx.schema.sentiment;
  const baseMessageOnly = !ctx.capabilities.cap_review_queue;
  const filters = baseMessageOnly ? ['Messages'] as CallFilter[] : FILTERS;
  const activeFilter: CallFilter = baseMessageOnly ? 'Messages' : filter;
  const rangeLabel = calls?.range?.label ?? 'This range';
  const rangeDescription = calls?.range?.description ?? 'this range';

  return (
    <div
      style={{
        padding: '44px 40px 64px 40px',
        maxWidth: 1320,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 28,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Eyebrow>{baseMessageOnly ? 'MESSAGE RECORD' : 'CALL RECORD'} · {ctx.branch.name.toUpperCase()}</Eyebrow>
          <H1>{baseMessageOnly ? 'Messages received' : 'Every call, understood'}</H1>
        </div>
        {calls ? (
          <Mono size={11} tracking={0.08}>
            {calls.total} {calls.total === 1 ? (baseMessageOnly ? 'MESSAGE' : 'CALL') : (baseMessageOnly ? 'MESSAGES' : 'CALLS')} · {rangeLabel.toUpperCase()}
            {!baseMessageOnly && calls.needFollowUp != null ? ` · ${calls.needFollowUp} NEED FOLLOW-UP` : ''}
          </Mono>
        ) : null}
      </div>

      {/* Filter chips + sentiment key */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
        {filters.map(f => {
          const selected = f === activeFilter;
          const disabled = f === 'Frustrated' && !sentimentReady;
          return (
            <button
              key={f}
              className="vp-reset"
              onClick={disabled ? undefined : () => onFilter(f)}
              aria-pressed={selected}
              title={disabled ? 'Needs calls.sentiment_label before it can filter anything.' : undefined}
              style={{
                padding: '7px 15px',
                borderRadius: radius.pill,
                fontSize: 13.5,
                border: `1px solid ${selected ? c.indigo : c.line}`,
                background: selected ? c.indigo : c.surface2,
                color: disabled ? 'rgba(11,18,32,0.28)' : selected ? c.surface2 : c.ink2,
                cursor: disabled ? 'not-allowed' : 'pointer',
              }}
            >
              {f}
            </button>
          );
        })}

        {!baseMessageOnly ? <span style={{ display: 'flex', alignItems: 'center', gap: 14, marginLeft: 'auto' }}>
          {sentimentReady ? (
            (['positive', 'neutral', 'frustrated'] as const).map(k => (
              <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 7, height: 7, borderRadius: radius.pill, background: sentimentColour[k] }} />
                <Mono size={10} tracking={0.08}>
                  {k.toUpperCase()}
                </Mono>
              </span>
            ))
          ) : (
            <Mono size={10} tracking={0.08} color={c.orange700}>
              SENTIMENT NOT RECORDED YET
            </Mono>
          )}
        </span> : null}
      </div>

      {error ? <Failure message={error} onRetry={onRetry} /> : null}

      {loading && !calls ? (
        <LoadingPanel label="LOADING CALLS" />
      ) : calls && calls.rows.length === 0 ? (
        <EmptyRange
          rangeLabel={`No calls · ${rangeLabel}`}
          title={
            activeFilter === 'All'
              ? `No calls for ${ctx.branch.name} ${rangeDescription}.`
              : `Nothing matches “${activeFilter}” ${rangeDescription}.`
          }
          body={
            activeFilter === 'All'
              ? `The assistant is on duty. Every call it answers lands here with the transcript and what the caller wanted. Try a wider window if you are looking for older calls.`
              : baseMessageOnly
                ? `Only messages received are shown in this Base archive.`
                : `Clear the filter to see every call ${rangeDescription}.`
          }
          actions={
            activeFilter === 'All' ? (
              <>
                <LinkButton onClick={() => onChangeRange('30d')} size={14}>
                  Show the last 30 days →
                </LinkButton>
                <LinkButton onClick={() => onChangeRange('90d')} size={14}>
                  Last 90 days →
                </LinkButton>
              </>
            ) : (
              <LinkButton onClick={() => onFilter(baseMessageOnly ? 'Messages' : 'All')} size={14}>
                {baseMessageOnly ? 'Messages only' : 'Clear the filter →'}
              </LinkButton>
            )
          }
        />
      ) : calls ? (
        <div
          style={{
            background: c.surface2,
            border: border.default,
            borderRadius: radius.card,
            overflow: 'hidden',
            boxShadow: shadow.card,
          }}
        >
          {/* Header row */}
          <div
            style={{
              boxSizing: 'border-box',
              display: 'grid',
              gridTemplateColumns: GRID,
              gap: 12,
              padding: '14px 20px',
              borderBottom: border.default,
              background: c.surface,
            }}
          >
            {['TIME', 'CALLER', 'CATEGORY', 'WHAT THEY WANTED', 'FOLLOW-UP'].map(h => (
              <span
                key={h}
                style={{
                  fontFamily: mono,
                  fontSize: 9.5,
                  letterSpacing: '0.12em',
                  color: c.ink3,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                {h}
              </span>
            ))}
          </div>

          {calls.rows.map(row => {
            const cat = categoryFor(row.outcome);
            return (
              <button
                key={row.id}
                className="vp-reset vp-row"
                onClick={() => onOpenCall(row.id)}
                style={{
                  boxSizing: 'border-box',
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: GRID,
                  gap: 12,
                  padding: '16px 20px',
                  borderBottom: `1px solid ${c.lineSoft}`,
                  alignItems: 'center',
                  textAlign: 'left',
                }}
              >
                <Mono size={12.5} color={c.ink2}>
                  {row.time}
                </Mono>

                <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                    {row.sentiment ? (
                      <span
                        style={{
                          width: 7,
                          height: 7,
                          flex: '0 0 7px',
                          borderRadius: radius.pill,
                          background: sentimentColour[row.sentiment],
                        }}
                      />
                    ) : (
                      <span style={{ width: 7, flex: '0 0 7px' }} />
                    )}
                    <span
                      style={{
                        fontSize: 14.5,
                        color: c.ink,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.name}
                    </span>
                  </span>
                  <span
                    style={{
                      fontFamily: mono,
                      fontSize: 11,
                      color: c.ink3,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      paddingLeft: 14,
                    }}
                  >
                    {row.number}
                  </span>
                </span>

                <span style={{ display: 'flex', minWidth: 0 }}>
                  <Pill bg={cat.bg} fg={cat.fg}>
                    {row.category}
                  </Pill>
                </span>

                <span
                  style={{
                    fontSize: 14,
                    color: c.ink2,
                    lineHeight: 1.5,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={row.summary}
                >
                  {row.summary}
                </span>

                <span style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0, alignItems: 'flex-start' }}>
                  <span
                    style={{
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      color: row.needsFollowUp ? c.orange700 : c.ink3,
                    }}
                  >
                    {row.status}
                  </span>
                  <Mono size={11}>{row.duration}</Mono>
                </span>
              </button>
            );
          })}

          {calls.nextCursor ? (
            <button
              className="vp-reset vp-row"
              onClick={onLoadMore}
              style={{ width: '100%', padding: '16px 20px', textAlign: 'center', fontSize: 13.5, color: c.indigo }}
            >
              {loading ? 'Loading…' : 'Load older calls'}
            </button>
          ) : null}
        </div>
      ) : null}

      {calls && calls.notReady.length > 0 ? (
        <NotReady
          compact
          title="Two columns on this table are waiting on call analysis."
          body="The caller-sentiment dot and the assistant's one-line summary are written when the transcript is analysed. Until then the summary column falls back to the typed outcome the assistant already recorded, and the dot is left blank."
          objects={calls.notReady}
        />
      ) : null}
    </div>
  );
}
